import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { BuilderMode, BuilderStep, SurveyFocus, SurveyForm, TemplatePreload, Question } from '../types';
import { surveyApi, USE_MOCK } from '@/api/client';
import type { PagedSurveyDto } from '@/api/types';
import { useOUItems, useGdprOuKey } from '@/hooks/useOUs';
import { buildOuTree, findOuNodePath, type OuNode } from '@/components/common/OuTree';
import {
  validateStepInfo,
  validateStepAudiencia,
  validateStepPreguntas,
  validateStepConfig,
} from '../validators';

/** Maps API questionType string back to builder Question type */
function fromBackendQuestionType(t: string): Question['type'] {
  switch (t) {
    case 'nps':  return 'nps';
    case 'open': return 'open';
    default:     return 'behaviors';
  }
}

/** Agrupa `ouIds` (planos, cualquier nivel) por su raíz real recorriendo el árbol completo ya
 *  cargado — reemplaza la heurística previa de coincidencia de prefijo de string (`startsWith`),
 *  que no es válida con ids reales tipo GUID. Un id que ya no existe en el catálogo actual se
 *  descarta silenciosamente (la OU fue eliminada), en vez de intentar reconciliarlo. */
export function splitOuIdsByRoot(
  tree: OuNode[],
  ouIds: string[],
  rootIds: ReadonlySet<string>,
): { audienceOUs: string[]; audienceSubOUs: Record<string, string[]> } {
  const audienceOUs: string[] = [];
  const audienceSubOUs: Record<string, string[]> = {};

  for (const ouId of ouIds) {
    if (rootIds.has(ouId)) {
      if (!audienceOUs.includes(ouId)) audienceOUs.push(ouId);
      if (!audienceSubOUs[ouId]) audienceSubOUs[ouId] = [];
      continue;
    }
    const nodePath = findOuNodePath(tree, ouId);
    if (!nodePath) continue;
    const rootNode = nodePath.find(pathNode => rootIds.has(pathNode.ou.id));
    if (!rootNode) continue;
    if (!audienceOUs.includes(rootNode.ou.id)) audienceOUs.push(rootNode.ou.id);
    const existingSubIds = audienceSubOUs[rootNode.ou.id] ?? [];
    audienceSubOUs[rootNode.ou.id] = [...existingSubIds, ouId];
  }

  return { audienceOUs, audienceSubOUs };
}

/** Reconstructs wizard audienceOUs + audienceSubOUs + destMode from the RN-009 DTO fields.
 * rootOuIds identifica cuáles ids de dto.ouIds son OUs raíz (type === 'OU' en Mongo) — ya no
 * es una lista fija de slugs (E-003: los ids reales son GUIDs de Azure AD/Mongo). */
function audienceContractToFormFields(dto: PagedSurveyDto, rootOuIds: ReadonlySet<string>, tree: OuNode[]): Partial<SurveyForm> {
  if (dto.audienceType === 'meeting') {
    return { destMode: 'meeting', sessionId: dto.targetMeetingId ?? '', destPeople: [], destOU: '', audienceOUs: [], audienceSubOUs: {} };
  }
  if (dto.audienceType === 'people') {
    return { destMode: 'people', sessionId: '', destPeople: dto.targetUserIds ?? [], destOU: '', audienceOUs: [], audienceSubOUs: {} };
  }
  if (dto.audienceType === 'ou' && dto.category === 'personalizada') {
    return { destMode: 'ou', destOU: dto.ouIds?.[0] ?? '', sessionId: '', destPeople: [], audienceOUs: [], audienceSubOUs: {} };
  }
  // audienceType === 'ou' (general survey)
  const ouIds = dto.ouIds ?? [];
  if (ouIds.length === 0 || ouIds.includes('global')) {
    return { audienceOUs: [], audienceSubOUs: {} };
  }
  return splitOuIdsByRoot(tree, ouIds, rootOuIds);
}

/** Populates SurveyForm from a fetched SurveyDto */
export function surveyDtoToForm(dto: PagedSurveyDto, current: SurveyForm, rootOuIds: ReadonlySet<string>, tree: OuNode[]): SurveyForm {
  const sortedQuestions = [...dto.questions].sort((a, b) => a.order - b.order);
  const questions: Question[] = sortedQuestions.map(q => {
    const sourceLang = q.sourceLang ?? 'es';
    return {
      id: q.id,
      type: fromBackendQuestionType(q.questionType),
      enfoque: (q.focusType as SurveyFocus) ?? 'individual',
      presentMode: (q.presentationMode as 'matrix' | 'individual') ?? 'matrix',
      fundamental: q.fundamentalId ?? '',
      coreBehavior: q.dailyHabitId ?? '',
      text: q.text[sourceLang] ?? Object.values(q.text)[0] ?? '',
      sourceLang,
      icon: '',
      options: [],
      includeNoObs: false,
    };
  });

  // Rebuild translations map: { lang: { "q_0": text, "q_1": text, ... } }
  // Keys must match the index-based keys that TranslationsSection writes ("q_0", "q_1", etc.).
  // El idioma origen de cada pregunta (q.sourceLang, no siempre "es") queda excluido de translations:
  // ese valor vive en Question.text, no en el mapa de traducciones.
  const translations: Record<string, Record<string, string>> = {};
  const translationsEnabled: Record<string, boolean> = {};
  for (let qi = 0; qi < sortedQuestions.length; qi++) {
    const q = sortedQuestions[qi]!;
    const sourceLang = q.sourceLang ?? 'es';
    for (const [lang, text] of Object.entries(q.text)) {
      if (lang === sourceLang) continue;
      if (!translations[lang]) translations[lang] = {};
      translations[lang][`q_${qi}`] = text;
      translationsEnabled[lang] = true;
    }
  }

  // Rebuild survey_name and survey_desc translation keys from backend metadata fields.
  for (const [lang, text] of Object.entries(dto.nameTranslations ?? {})) {
    if (!translations[lang]) translations[lang] = {};
    translations[lang]['survey_name'] = text;
    translationsEnabled[lang] = true;
  }
  for (const [lang, text] of Object.entries(dto.descriptionTranslations ?? {})) {
    if (!translations[lang]) translations[lang] = {};
    translations[lang]['survey_desc'] = text;
    translationsEnabled[lang] = true;
  }

  return {
    ...current,
    name: dto.name,
    description: dto.description ?? '',
    surveyType: (dto.category as SurveyForm['surveyType']) ?? current.surveyType,
    ...audienceContractToFormFields(dto, rootOuIds, tree),
    startDate: dto.startDate ? dto.startDate.split('T')[0] : current.startDate,
    expirationDate: dto.expiresAt ? dto.expiresAt.split('T')[0] : '',
    questions,
    translations,
    translationsEnabled,
    campaignWeight: dto.campaignWeight ?? current.campaignWeight,
    activeDays: dto.activeDays ?? current.activeDays,
    boostUrgente: dto.boostUrgente ?? current.boostUrgente,
    boostDays: dto.boostDays ?? current.boostDays,
    minRespondents: dto.minRespondents ?? current.minRespondents,
    maxRespondents: dto.maxRespondents ?? current.maxRespondents,
  };
}

const MIN_PREVIEW = 240;
const MAX_PREVIEW = 720;
const DEFAULT_PREVIEW = 420;

export function useSurveyBuilder(mode: BuilderMode, onClose: () => void, template?: TemplatePreload, surveyId?: string) {
  const { t } = useTranslation();

  // Resolve the ID to load when in edit mode
  const editId = surveyId ?? (template?.mode === 'edit' ? String(template.id) : undefined);

  // activeOnly: false — igual que useGdprOuKey(), esto reconstruye la audiencia YA GUARDADA de la
  // encuesta (no el picker en vivo, que tiene su propio useOUs() en StepAudiencia.tsx), así que no
  // debe depender del estado activo/inactivo actual de la OU o se perdería en silencio del audienceOUs
  // reconstruido una OU desactivada después de crear la encuesta.
  const { data: allOuItems = [] } = useOUItems({ activeOnly: false });
  const gdprOuKey = useGdprOuKey();
  const rootOuIds = useMemo(
    () => new Set(allOuItems.filter(item => item.type === 'OU').map(item => item.id)),
    [allOuItems],
  );
  const ouTree = useMemo(() => buildOuTree(allOuItems), [allOuItems]);

  const { data: loadedSurvey } = useQuery({
    queryKey: ['survey', editId],
    queryFn: () => surveyApi.getSurveyById(editId!),
    enabled: !USE_MOCK && !!editId,
    staleTime: Infinity,
  });

  const [step, setStep] = useState<BuilderStep>(0);
  const [maxStepReached, setMaxStepReached] = useState<BuilderStep>(0);
  const [form, setForm] = useState<SurveyForm>(() => ({
    name: template ? template.name : '',
    description: template ? template.description : '',
    surveyType: template ? 'general' : '',
    startDate: template?.startDate
      ? template.startDate.split('T')[0]
      : new Date().toISOString().split('T')[0],
    expirationDate: template?.expirationDate ? template.expirationDate.split('T')[0] : '',
    activeDays: [0, 1, 2, 3, 4],
    audienceOUs: [],
    audienceSubOUs: {},
    anonymous: false,
    campaignWeight: 5,
    boostUrgente: false,
    boostDays: 3,
    minRespondents: 3,
    maxRespondents: 10,
    questions: [],
    translations: {},
    translationsEnabled: {},
    destMode: 'meeting',
    sessionId: '',
    destPeople: [],
    destOU: '',
  }));

  // Populate form once the existing survey is fetched (edit mode)
  useEffect(() => {
    if (loadedSurvey) {
      setForm(current => surveyDtoToForm(loadedSurvey, current, rootOuIds, ouTree));
    }
  }, [loadedSurvey, rootOuIds, ouTree]);

  const [showExitModal, setShowExitModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [previewIconTarget, setPreviewIconTarget] = useState<number | null>(null);
  const [enfoqueFilter, setEnfoqueFilter] = useState<'all' | 'individual' | 'colectivo'>('all');
  // Expandida por defecto solo en "Preguntas" (índice 2 de BUILDER_STEPS); colapsada en el
  // resto (Info/Audiencia/Configuración/Publicar). El usuario puede mostrarla/ocultarla
  // manualmente mientras permanece en un paso — al cambiar de paso se reaplica el default.
  const [previewVisible, setPreviewVisible] = useState(step === 2);
  const [previewWidth, setPreviewWidth] = useState(DEFAULT_PREVIEW);

  const dragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(DEFAULT_PREVIEW);
  const handleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const delta = startX.current - e.clientX;
      const newW = Math.min(MAX_PREVIEW, Math.max(MIN_PREVIEW, startW.current + delta));
      setPreviewWidth(newW);
    };
    const onMouseUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      handleRef.current?.classList.remove('dragging');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  useEffect(() => {
    setPreviewVisible(step === 2);
  }, [step]);

  const onHandleMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    startX.current = e.clientX;
    startW.current = previewWidth;
    handleRef.current?.classList.add('dragging');
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  }, [previewWidth]);

  const stepValidators = useMemo<Array<() => string[]>>(() => [
    () => validateStepInfo(form, t),
    () => validateStepAudiencia(form, t),
    () => validateStepPreguntas(form, t),
    () => validateStepConfig(form, t, gdprOuKey),
  ], [form, t, gdprOuKey]);

  /** Corre los validadores de todos los pasos previos a `targetIndex` (0..targetIndex-1) y
   *  acumula sus errores. Se usa tanto para saltos de pestaña hacia adelante como para la
   *  validación completa previa a Guardar/Publicar, ya que un salto directo (p. ej. en modo
   *  edit, donde `canJumpToStep` no restringe destino) puede omitir pasos nunca visitados. */
  const validateUpTo = useCallback((targetIndex: number): string[] => {
    const errors: string[] = [];
    for (let stepIndex = 0; stepIndex < targetIndex; stepIndex++) {
      const validator = stepValidators[stepIndex];
      if (validator !== undefined) errors.push(...validator());
    }
    return errors;
  }, [stepValidators]);

  const runFullValidation = useCallback((): boolean => {
    const errors = validateUpTo(stepValidators.length);
    if (errors.length > 0) { setValidationErrors(errors); return false; }
    return true;
  }, [validateUpTo, stepValidators.length]);

  const goNext = useCallback(() => {
    const validator = stepValidators[step];
    if (validator !== undefined) {
      const errors = validator();
      if (errors.length > 0) { setValidationErrors(errors); return; }
    }
    if (step < 4) {
      const next = (step + 1) as BuilderStep;
      setStep(next);
      setMaxStepReached(current => (next > current ? next : current));
    }
  }, [step, stepValidators]);

  const goPrev = useCallback(() => {
    if (step > 0) setStep((step - 1) as BuilderStep);
  }, [step]);

  /** Navegación directa a una pestaña (click en el header de pasos). Retroceder siempre está
   *  permitido sin validar; avanzar valida todos los pasos intermedios (0..target-1) para que
   *  un salto directo no se salte, por ejemplo, la fecha vencida detectada en el paso Info. */
  const goToStep = useCallback((target: BuilderStep) => {
    if (target <= step) { setStep(target); return; }
    const errors = validateUpTo(target);
    if (errors.length > 0) { setValidationErrors(errors); return; }
    setStep(target);
    setMaxStepReached(current => (target > current ? target : current));
  }, [step, validateUpTo]);

  const handleClose = useCallback(() => setShowExitModal(true), []);
  const handleExitCancel = useCallback(() => setShowExitModal(false), []);
  const handleExitConfirm = useCallback(() => onClose(), [onClose]);
  const clearValidationErrors = useCallback(() => setValidationErrors([]), []);

  return {
    // state
    step, setStep,
    maxStepReached,
    form, setForm,
    showExitModal,
    validationErrors,
    previewIconTarget, setPreviewIconTarget,
    enfoqueFilter, setEnfoqueFilter,
    previewVisible, setPreviewVisible,
    previewWidth,
    handleRef,
    // derived
    isLast: step === 4,
    editId,
    mode,
    // handlers
    goNext, goPrev, goToStep,
    runFullValidation,
    handleClose, handleExitCancel, handleExitConfirm,
    clearValidationErrors,
    onHandleMouseDown,
  };
}
